import { useCallback, useEffect, useState } from 'react';
import { DropZone, LegacyStack, Modal, Thumbnail, Text, ProgressBar, Toast, Loading, BlockStack, List } from '@shopify/polaris';
import { SaveIcon, NoteIcon, XIcon } from '@shopify/polaris-icons';
import axios from 'axios';
import { useImportTransactions } from 'queries/transactions.query';
import { useNotification } from 'context/NotificationContext';
const REACT_APP_AJAX_UPLOAD_URL = process.env.REACT_APP_AJAX_UPLOAD_URL;

export default function TransactionsUploadModal({ onClose, show }: { onClose: () => void; show: boolean }) {
  const { mutateAsync: importTransactions, reset: resetUploadedData, isPending: importLoading, data: importSuccess } = useImportTransactions();
  const { addNotification } = useNotification();
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(0);
  const [internalNotification, setInternalNotification] = useState(null);
  const clearError = useCallback(() => {
    setInternalNotification(null);
  }, []);

  /** initial load */

  useEffect(() => {
    setFiles([]);
    resetUploadedData();
  }, [show]);

  /**
   * return to main progress file, callback
   */
  const uploadSuccess = async (mediaRep: any) => {
    let { media_id } = mediaRep;
    try {
      await importTransactions({
        media_id: media_id,
      });
      addNotification('info', 'Tải lên thành công!');
    } catch (e) {
      addNotification('error', 'File không đúng định dạng được yêu cầu!');
    }
  };

  function uploadFail(rep: any) {
    setInternalNotification('Upload file bị lỗi, vui lòng thử lại.');
  }

  const onCloseCallback = () => {
    clearError();
    onClose();
  };

  /**
   * Step2. save to server
   *
   * @param {File} file
   */

  async function saveToServer(file: File) {
    const fd = new FormData();
    fd.append('files', file);
    try {
      let result = await axios.post(REACT_APP_AJAX_UPLOAD_URL, fd, {
        onUploadProgress: (progressEvent: any) => {
          let progress_percent = Math.floor(progressEvent.loaded / progressEvent.total) * 100;
          setLoading(progress_percent);
        },
      });
      uploadSuccess(result.data);
      setLoading(0);
    } catch (_) {
      setLoading(0);
      uploadFail(_);
    }
  }

  const startUpload = useCallback(() => {
    if (!files) return;
    for (let _file of files) {
      saveToServer(_file);
    }
  }, [files]);

  const handleDropZoneDrop = useCallback((_dropFiles: File[], acceptedFiles: File[], _rejectedFiles: File[]) => setFiles(() => acceptedFiles), []);

  const uploadedFiles = files.length > 0 && (
    <div style={{ padding: '0' }}>
      <BlockStack gap="400">
        {files.map((file, index) => (
          <LegacyStack alignment="center" key={index}>
            <Thumbnail size="small" alt={file.name} source={NoteIcon} />
            <div>
              {file.name}{' '}
              <Text variant="bodySm" as="p">
                {file.size} bytes
              </Text>
            </div>
            {loading > 0 ? <ProgressBar progress={loading} size="small" tone="success" /> : null}
          </LegacyStack>
        ))}
      </BlockStack>
    </div>
  );

  const toastMarkup = internalNotification ? <Toast content={internalNotification} error onDismiss={clearError} /> : null;

  return (
    <>
      {toastMarkup}
      {importLoading ? <Loading /> : null}
      <Modal
        open={show}
        onClose={() => onCloseCallback()}
        title="Tải lên giao dịch qua file CSV"
        primaryAction={{
          content: 'Bắt đầu',
          loading: loading > 0,
          icon: SaveIcon,
          onAction: () => startUpload(),
        }}
        secondaryActions={[
          {
            content: 'Hủy',
            icon: XIcon,
            onAction: () => onCloseCallback(),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <DropZone allowMultiple={false} accept=".csv" errorOverlayText="File type must be .csv" type="file" onDrop={handleDropZoneDrop}>
              <DropZone.FileUpload />
            </DropZone>

            {uploadedFiles}

            <Text as="p">* Hành động này không ghi đè dữ liệu cũ.</Text>
            <Text as="p">* File CSV đảm bảo có các cột sau đây:</Text>
            <List>
              <List.Item>no - Số thứ tự</List.Item>
              <List.Item>content - Nội dung chuyển khoản, đúng format là: TT ve MAVE8KYTU</List.Item>
              <List.Item>amount - Số tiền</List.Item>
              <List.Item>accounting_date - Định dạng ngày chấp nhận: DD/MM/YYYY HH:mm:ss</List.Item>
            </List>
            <Text as="p">
              * Bạn phải đảm bảo mã đơn hàng ở cuối mô tả, nếu không sẽ không thể nhập được. Ngoài ra chú ý tới tên cột, cột có thể có nhiều cột,
              nhưng tên cột phải như đề xuất.
            </Text>

            {importSuccess && (
              <Text as="p" tone="success">
                Kết quả: Tổng{' '}
                <Text as="span" fontWeight="bold" variant="bodyLg">
                  {importSuccess.total}
                </Text>{' '}
                giao dịch được nhập, trong đó{' '}
                <Text as="span" fontWeight="bold" variant="bodyLg">
                  {importSuccess.total_success}
                </Text>{' '}
                giao dịch là hợp lệ,{' '}
                <Text as="span" fontWeight="bold" variant="bodyLg">
                  {importSuccess.total_error}
                </Text>{' '}
                là lỗi chưa nhập lên.
              </Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}

